import {
    useGetPaymentStatistics,
    useGetTransactionHistory,
    useGetBalanceHistory,
    useGetDetailedTransactionHistory,
    useCancelWithdraw,
    useAddBeforeWithdrawHook,
    useGetBeforeWithdrawHandler
} from 'js/hooks/payments';
import { useMemo } from 'react';
import {
    useDeposit,
    useGetPaymentMethodAccounts,
    useGetPaymentMethodList,
    useWithdraw
} from 'server';

export const usePaymentsPublicAPI = () => {
    const getPaymentStatistics = useGetPaymentStatistics();
    const getTransactionHistory = useGetTransactionHistory();
    const getBalanceHistory = useGetBalanceHistory();
    const getDetailedTransactionHistory = useGetDetailedTransactionHistory();
    const cancelWithdraw = useCancelWithdraw();
    const deposit = useDeposit();
    const withdraw = useWithdraw();
    const getPaymentMethodAccounts = useGetPaymentMethodAccounts();
    const getPaymentMethodList = useGetPaymentMethodList();
    const addBeforeWithdrawHook = useAddBeforeWithdrawHook();
    const getBeforeWithdrawHandler = useGetBeforeWithdrawHandler();

    return useMemo(() => ({
        /**
         * Returns statistics about player payments.
         * @memberOf Playtech.API.payments
         * @function
         * @returns {Promise<{Object}>} { deposits: {firstDepositDate: string, lastDepositDate: string, totalDepositAmount: string, totalDepositCount: string}, withdrawals: {approvedWithdrawRequestsCount: string, lastWithdrawRequestDate: string, pendingWithdrawRequestsCount: string} }
         * @example
         * Playtech.API.payments.getPaymentStatistics().then((data) => { ... });
         */
        getPaymentStatistics,
        /**
         * Returns customizable list of all payment transactions for a user based on criteria. See [ims docs]{@link https://integration.playtech.com/apis/openapi/services/PaymentService/operations/WebAPI_GetTransactionHistory.html}
         * @memberOf Playtech.API.payments
         * @function
         * @param {Object} options
         * @param {string} [options.type] Values: "Deposit"|"Withdraw". Type of transactions
         * @param {string} [options.status] Values: "Pending"|"Waiting"|"Approved"|"Declined"|"Cancelled". Status of the transactions
         * @param {string} [options.startTime] Filter by date in ISO8601. Cannot be further than 3 months ago
         * @param {string} [options.endTime] Filter by date in ISO8601 format. Maximum 7 days from startTime
         * @param {string} [options.corrections] To include correction transactions or not
         * @returns {Promise<{payments: Object, maxResultsExceeded: boolean}>}
         * @example
         * Playtech.API.payments.getTransactionHistory({type: 'Deposit', status: 'Approved'}).then((data) => { ... });
         */
        getTransactionHistory,
        /**
         * Returns balance change history of player's published balances. See [ims docs]{@link https://integration.playtech.com/apis/openapi/services/WalletService/operations/GetBalanceHistory.html}
         * @memberOf Playtech.API.payments
         * @function
         * @param {Object} options
         * @param {string} [options.startDate]
         * @param {string} [options.endDate]
         * @param {string} [options.clientType]
         * @param {string} [options.orderByDate]
         * @param {string} [options.returnSummary]
         * @param {string} [options.transactionGroups]
         * @param {boolean} [options.ignorePlayerViewConf]
         * @param {boolean} [options.ignoreStartingEndingBalances]
         * @param {Object} [options.pagerRequest]
         * @returns {Promise<{Object}>} { pagerResponse: Object, endingBalances: Array, startingBalances: Array, walletTransactions: Array, defaultTypeNames: Array, usedTagsDefaults: Array, usedTemplates: Array, summary: Object }
         * @example
         * Playtech.API.payments.getBalanceHistory(params).then((data) => { ... });
         */
        getBalanceHistory,
        /**
         * Returns information about player's transactions. See [ims docs]{@link https://integration.playtech.com/apis/openapi/services/PaymentService/operations/GetTransactionHistoryGalaxy.html}
         * @memberOf Playtech.API.payments
         * @function
         * @param {Object} options
         * @param {string} [options.clientPlatform]
         * @param {string} [options.cancellations]
         * @param {string} [options.corrections]
         * @param {string} [options.endTime]
         * @param {string} [options.excludePaymentRequests]
         * @param {string} [options.historyRequestSummary]
         * @param {Object} options.pagingRequestParams
         * @param {boolean} options.pagingRequestParams.returnTotalCount
         * @param {number} options.pagingRequestParams.pageSize
         * @param {string} options.pagingRequestParams.pageId
         * @param {boolean} options.pagingRequestParams.returnAllPageIds
         * @param {string} [options.reason]
         * @param {string} [options.startTime]
         * @param {string} [options.status]
         * @param {string} [options.transactionCode]
         * @param {string} [options.type]
         * @returns {Promise<{pagingResponseParams: Object, transactions: Array, historyResponseSummary: Object}>}
         * @example
         * Playtech.API.payments.getDetailedTransactionHistory(params).then((data) => { ... });
         */
        getDetailedTransactionHistory,
        /**
         * Returns statistics about player payments. See [ims docs]{@link https://integration.playtech.com/apis/openapi/services/PaymentService/operations/WebAPI_CancelWithdraw.html}
         * @memberOf Playtech.API.payments
         * @function
         * @param {Object} options
         * @param {string} [options.transactionCode]
         * @param {string} [options.reason]
         * @returns {Promise<void>}
         * @example
         * Playtech.API.payments.cancelWithdraw(params).then(() => { ... });
         */
        cancelWithdraw,
        /**
         * Creates deposit request.
         * @memberOf Playtech.API.payments
         * @function
         * @param {Object} options
         * @returns {Promise<void>}
         * @example
         * Playtech.API.payments.deposit(params).then(() => { ... });
         */
        deposit,
        /**
         * Creates withdraw request.
         * @memberOf Playtech.API.payments
         * @function
         * @param {Object} options
         * @returns {Promise<void>}
         * @example
         * Playtech.API.payments.withdraw(params).then(() => { ... });
         */
        withdraw,
        /**
         * This method provides list of payment methods accounts previously used and available for the player.
         * @memberOf Playtech.API.payments
         * @function
         * @param {Object} options
         * @returns {Promise<void>}
         * @example
         * Playtech.API.payments.getPaymentMethodAccounts(params).then(() => { ... });
         */
        getPaymentMethodAccounts,
        /**
         * This method provides list of available payment methods for particular player based on real time analysis of player's profile and dynamic business settings managed in Payments backend.
         * @memberOf Playtech.API.payments
         * @function
         * @param {Object} options
         * @returns {Promise<void>}
         * @example
         * Playtech.API.payments.getPaymentMethodList(params).then(() => { ... });
         */
        getPaymentMethodList,
        /**
         * Add callback to modify the data that is sent to IMS during withdraw.
         * It must return all arguments that it was given.
         * @memberOf Playtech.API.payments
         * @function
         * @param {Function} callback ({ specificParams }) => {... return { specificParams }} request from withdraw form
         * @example
         * Playtech.API.payments.addBeforeWithdrawHook(({ specificParams }) => {
         *     // modify field6 field right before withdraw request is sent
         *     specificParams.field6 = '123';
         *
         *     return { specificParams };
         * });
         */
        addBeforeWithdrawHook,
        /**
         * @private
         * This is only for Backbone withdraw.method.model module.
         */
        getBeforeWithdrawHandler
    }), [
        deposit,
        withdraw,
        getPaymentMethodAccounts,
        getPaymentMethodList,
        getPaymentStatistics,
        getTransactionHistory,
        getBalanceHistory,
        getDetailedTransactionHistory,
        cancelWithdraw,
        addBeforeWithdrawHook,
        getBeforeWithdrawHandler
    ]);
};
